/*
  makeStyles.js
*/

// Npm.
import { useMemo } from 'react';
import { css, cx } from '@emotion/css';

// Hooks.
import useTheme from '../hooks/useTheme';

/**
 * Helper to calculate CSS classNames for an object.
 */
function getResult(obj) {

  // TODO: Coult optimize this by caching the result globally,
  // and this is already what the "css" function does.
  // It will just mean that "theme" would need to be kept relatively constant
  // so that light vs dark them won't keep adding stuff to the cache.

  const result = {};

  if (typeof obj === 'object' && obj != null) {
    for (const [key, value] of Object.entries(obj)) {
      if (value) {
        result[key] = css(value);
      }
    }
  }

  return result;

}

/**
 * Hook that returns classNames for styling.
 * Accepts an object with names, similar to the "makeStyles" function in MUI.
 */
function useStylesWithObject(obj) {

  return useMemo(() => getResult(obj), [obj]);

}

/**
 * Hook similar to above hook, but uses a function instead of an object
 * that takes the theme as a parameter.
 */
function useStylesWithFunction(func) {

  const theme = useTheme();

  return useMemo(() => getResult(func(theme)), [func, theme]);

}

/**
 * High-order hook for making "useStyles" hook.
 *
 * It's important that this is high-order to keep the "objectOrFunc" identity constant
 * which will prevent constant re-calculation of the CSS classes.
 *
 * @param {Object} objectOrFunc - object or function used to calculate the CSS with.
 *
 * @returns {Function} hook.
 */
function makeStyles(objectOrFunction) {
  if (typeof objectOrFunction === 'function') {

    return function useStyles() {
      return useStylesWithFunction(objectOrFunction);
    }

  } else {

    return function useStyles() {
      return useStylesWithObject(objectOrFunction);
    }

  }
}

export default makeStyles;
export {
  makeStyles,
  cx,
};
